import CookieService from 'utils/miscUtils/CookieService';
import type CustomWindow from 'interfaces/Utils';
import type { MyFavorites } from 'interfaces/Account';
import type { MyAccountDataResponse } from 'lib/personalizationApi/entities/MyAccountDataResponse';
import getMyAccountData from 'lib/personalizationApi/getMyAccountData';
import type { AccessResponse } from 'services/Piano/entities/PianoCallbackParams';

declare let window: CustomWindow;

export interface SubscriberContext {
  authorNames: string[];
  isPaused: boolean;
  email: string;
  hasAccess: boolean | null;
  isLoggedIn: boolean | null;
  myFavorites: MyFavorites;
  pianoUid: string;
  subAudience: string;
  topicNames: string[];
  subscribedMessageTypeIds: number[];
  myAccountData: MyAccountDataResponse | null;
  fetchError: boolean;
}

export const defaultSubscriberContext: SubscriberContext = {
  authorNames: [],
  email: '',
  fetchError: false,
  hasAccess: null,
  isLoggedIn: null,
  myAccountData: null,
  myFavorites: [],
  pianoUid: '',
  subAudience: '',
  subscribedMessageTypeIds: [],
  topicNames: [],
  isPaused: false,
};

export class Subscriber {
  email: string;

  pianoUid: string;

  isLoggedIn: boolean | null;

  hasAccess: boolean | null;

  subAudience: string;

  campaignId: number;

  templateId: number;

  topicNames: string[];

  authorNames: string[];

  subscribedMessageTypeIds: number[];

  myFavorites: MyFavorites;

  connect = false;

  myAccountData: MyAccountDataResponse | null;

  fetchError = false;

  isPaused = false;

  constructor() {
    this.email = '';
    this.pianoUid = '';
    this.isLoggedIn = false;
    this.hasAccess = false;
    this.subAudience = '';
    this.campaignId = 0;
    this.templateId = 0;
    this.topicNames = [];
    this.authorNames = [];
    this.myFavorites = [];
    this.subscribedMessageTypeIds = [];
    this.myAccountData = null;
    this.fetchError = false;
    this.isPaused = false;
  }

  public async init() {
    if (window?.tp?.user?.isUserValid()) {
      this.getLoggedIn();
      this.getEmail();
      this.getPianoUid();

      if (this.pianoUid) {
        await this.fetchMyAccountData()
          .then(() => {
            this.fetchError = false;
          })
          .catch(() => {
            this.fetchError = true;
          });
        this.isPaused = await this.checkIsPaused(this.pianoUid);
      }
      await this.checkAccess();
    }
    this.checkConnect();
    this.getSubAudience();
  }

  private getLoggedIn(): void {
    this.isLoggedIn = window.tp.user.isUserValid();
  }

  private getEmail(): void {
    this.email = window.tp.pianoId.getUser().email;
    CookieService.set('userEmail', this.email);
  }

  private getPianoUid(): void {
    this.pianoUid = window.tp.pianoId.getUser().uid;
    CookieService.set('pianoUid', this.pianoUid);
  }

  private async checkAccess() {
    const rid: string = process.env.PIANO_RID_DIGITAL || 'default';
    const response: AccessResponse = await new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      window.tp.api.callApi('/access/check', { rid }, (data: AccessResponse) => {
        if (data) {
          resolve(data);
        } else {
          reject(new Error('Check access failed'));
        }
      });
    });
    this.hasAccess = response?.access?.granted;
    CookieService.set('pianoAccess', this.hasAccess.toString());
  }

  private getSubAudience(): void {
    const subAudience = CookieService.get('_pc_segment');
    if (subAudience) {
      this.subAudience = subAudience;
    }
  }

  private checkConnect(): void {
    const connectCookie = CookieService.get('_pc_fcsub');
    if (connectCookie === 'true') {
      this.hasAccess = true;
      this.connect = true;
    }
  }

  private async fetchMyAccountData() {
    try {
      this.myAccountData = await getMyAccountData(this.pianoUid);
    } catch (error) {
      throw error;
    }
  }

  // This checks from Recurly, which isn't consistent with the Piano approach for the Pages Router
  // However, this will work and maintains a check from the source of truth.
  private async checkIsPaused(pianoUid: string): Promise<boolean> {
    try {
      const response = await fetch('/api/checkUserSubscriptionStatus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pianoUid }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error with status ${response.status}`);
      }
      const data = await response.json();
      const isPaused = data.status === 'Paused';
      CookieService.set('pianoPaused', isPaused.toString());
      return isPaused;
    } catch (_error) {
      // Logging handled in API route if an error occurs
      return false;
    }
  }
}

export const validatePianoCookie = (): boolean => CookieService.get('__idr') === '1';
